import React, { useState } from 'react';
import { formatDate, toLocalISO } from '../../utils/dateFormat';
import { useVatMode } from '../../hooks/useVatMode';
import { getOrderNumber } from '../../utils/orderTitle';
import { hireRateDisplay } from '../../utils/hireCost';
import HireBulkDialogFrame from './HireBulkDialogFrame';
// 💷 ONE price choice, shared with the single-line Mark Off Hired dialog — the
// component, the prefill, the follow-the-date rule and the submit builder are
// IMPORTED, never copied (`npm run check-duplication`).
import OffHirePriceChoice, {
  initialPriceChoiceState,
  buildOffHirePriceChoice,
  priceChoiceAfterDateChange,
} from './OffHirePriceChoice';
// The same "When did it come back?" chooser as the single-line dialog (U2).
import OffHireReturnDateChoice, { initialReturnDateState, returnDateOf } from './OffHireReturnDate';

/**
 * OFF HIRE ALL — one return date for every hire line of an order that is not
 * already off-hired, each line with its own price choice, ONE confirm (owner
 * rulings 2026-09-28: *"the main header action menu to have off hire all"*,
 * *"yes go with the recommended popup"*).
 *
 * 🔑 The server is `POST /api/hires/orders/:orderId/off-hire-all` and it is
 * ALL-OR-NOTHING: this dialog sends `item_ids` (the lines it SHOWED — the
 * server refuses 409 if that set has changed) and `adjustments` only for the
 * lines whose choice moves money. OFF-HIRE NEVER AUTO-REPRICES: a line left on
 * "Keep original" (the default) moves no money, however its dates read.
 *
 * ⚖️ Per line, the PRICE CHOICE (owner 2026-09-28: *"show the original
 * estimated cost, and the current off hire price if early or late and then the
 * user can decide"*; default *"keep original"*) is `OffHirePriceChoice` — the
 * same component the single-line dialog renders, so the two cannot disagree.
 *
 * `lines` are hire-list rows (`GET /api/hires` — `item_id`, `line_total`,
 * `adjustment_total`, dates, rates), already narrowed by the caller to this
 * order's lines still on hire. `onConfirm(body)` returns a promise; `error` is
 * the caller's last server refusal, shown above the buttons.
 */

/** Per-line test ids; the Set-final-price parts keep their pre-choice names. */
const LEGACY_PARTS = { amount: 'amount', note: 'note', preview: 'preview', error: 'line-error' };
const lineTestId = (id) => (part) => `offhire-all-${LEGACY_PARTS[part] || part}-${id}`;

function OffHireAllDialog({ order, lines, onConfirm, onCancel, loading = false, error = '' }) {
  const { formatMoney } = useVatMode();
  // 🔀 2026-10-19 (U2): "When did it come back?" replaces this dialog's own date
  // picker too. ONE return_date goes to the server for every line, so "As
  // booked" is offered only when every line shares one hire_to (coordinator,
  // 2026-10-19); otherwise Today is the default, as before.
  const [today] = useState(() => toLocalISO(new Date()));
  const sharedHireTo = (() => {
    const ends = new Set((lines || []).map((l) => l.hire_to || ''));
    return ends.size === 1 && !ends.has('') ? [...ends][0] : null;
  })();
  const [returnState, setReturnState] = useState(() => initialReturnDateState({ hireTo: sharedHireTo, presetDate: null, today }));
  const returnDate = returnDateOf(returnState, sharedHireTo, today);
  const [dateError, setDateError] = useState('');
  // Per line, keyed by item_id: the price-choice state (`initialPriceChoiceState`).
  const [choices, setChoices] = useState({});

  if (!order || !lines) return null;

  const stateOf = (id) => choices[id] || initialPriceChoiceState();
  const patch = (id, next) => setChoices((prev) => ({ ...prev, [id]: { ...(prev[id] || initialPriceChoiceState()), ...next } }));

  const onDateChange = (nextState) => {
    const next = returnDateOf(nextState, sharedHireTo, today);
    setReturnState(nextState);
    setDateError('');
    // Every untouched Set-final-price box follows the date — the single-line rule.
    setChoices((prev) => {
      const out = { ...prev };
      lines.forEach((line) => {
        const s = prev[line.item_id];
        // `returnDate` = the date being left: a bucket crossing resets (ruling A).
        if (s) out[line.item_id] = priceChoiceAfterDateChange(line, s, next, formatMoney, returnDate);
      });
      return out;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!returnDate) {
      setDateError('Choose the date these items came back.');
      return;
    }
    const early = lines.find((l) => l.hire_from && returnDate < l.hire_from);
    if (early) {
      setDateError(`The return date is before "${early.description}" started (${formatDate(early.hire_from)}).`);
      return;
    }
    const adjustments = [];
    const errors = {};
    let owedId = null;
    lines.forEach((line) => {
      const built = buildOffHirePriceChoice(line, returnDate, stateOf(line.item_id), formatMoney);
      if (built.noteOwed) { if (owedId === null) owedId = line.item_id; }
      else if (built.error) errors[line.item_id] = built.error;
      else if (built.adjustment) adjustments.push({ item_id: line.item_id, ...built.adjustment });
    });
    // A note owed: Confirm stays live and nudges the FIRST owing line's note box
    // (shake, flash, focus) instead of submitting — the single-line rule.
    if (owedId !== null) {
      patch(owedId, { nudge: (stateOf(owedId).nudge || 0) + 1 });
      return;
    }
    if (Object.keys(errors).length) {
      Object.entries(errors).forEach(([id, msg]) => patch(id, { error: msg }));
      return;
    }
    onConfirm({
      return_date: returnDate,
      item_ids: lines.map((l) => l.item_id),
      ...(adjustments.length ? { adjustments } : {}),
    });
  };

  const count = lines.length;
  const noun = count === 1 ? 'line' : 'lines';

  return (
    <HireBulkDialogFrame
      testIdPrefix="offhire-all"
      title="Off hire all"
      subtitle={`${count} hire ${noun} still on hire on ${getOrderNumber(order)}`}
      tone="green"
      onSubmit={handleSubmit}
      onCancel={onCancel}
      loading={loading}
      error={error}
      footnote="All or nothing: if any line cannot be off-hired, none are."
      confirmLabel={`Off hire ${count} ${noun}`}
      busyLabel="Off hiring…"
    >
            <div>
              <OffHireReturnDateChoice
                hireTo={sharedHireTo}
                today={today}
                state={returnState}
                onChange={onDateChange}
                tid={(part) => `offhire-all-${part}`}
              />
              <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                Applies to every line below.
              </p>
              {dateError && (
                <p className="text-xs mt-1 text-red-600 dark:text-red-400" data-testid="offhire-all-date-error">{dateError}</p>
              )}
            </div>

            <ul className="divide-y divide-gray-200 dark:divide-gray-700 border rounded-lg border-gray-200 dark:border-gray-700">
              {lines.map((line) => {
                const id = line.item_id;
                return (
                  <li key={id} className="px-3 py-3 space-y-2" data-testid={`offhire-all-line-${id}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-0 truncate">{line.description}</span>
                      <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400 shrink-0">{hireRateDisplay(line, formatMoney)}</span>
                    </div>
                    <OffHirePriceChoice
                      item={line}
                      returnDate={returnDate}
                      state={stateOf(id)}
                      onChange={(next) => patch(id, next)}
                      tid={lineTestId(id)}
                      formatMoney={formatMoney}
                    />
                  </li>
                );
              })}
            </ul>
    </HireBulkDialogFrame>
  );
}

export default OffHireAllDialog;
